import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  Dispatch,
  SetStateAction,
} from 'react'
import {LayoutSplashScreen} from '../../../../_metronic/layout/core'
import {AuthModel, UserModel} from './_models'
import * as authHelper from './AuthHelpers'
import {WithChildren} from '../../../../_metronic/helpers'

const ONE_DAY_MS = 24 * 60 * 60 * 1000

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: UserModel | undefined
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
  logout: () => void
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => {},
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({children}) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>()

  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth)
    if (auth) {
      authHelper.setAuth(auth)
    } else {
      authHelper.removeAuth()
    }
  }

  const logout = () => {
    saveAuth(undefined)
    setCurrentUser(undefined)
    sessionStorage.clear()
  }

  // Auto-logout after 1 day
  useEffect(() => {
    const checkExpiry = () => {
      const loginTimestamp = sessionStorage.getItem('loginTimestamp')
      if (loginTimestamp) {
        const elapsed = Date.now() - parseInt(loginTimestamp, 10)
        if (elapsed > ONE_DAY_MS) {
          logout()
        }
      }
    }
    checkExpiry()
    const interval = setInterval(checkExpiry, 60 * 1000)
    return () => clearInterval(interval)
    // eslint-disable-next-line
  }, [])

  return (
    <AuthContext.Provider value={{auth, saveAuth, currentUser, setCurrentUser, logout}}>
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<WithChildren> = ({children}) => {
  const {logout} = useAuth()
  const [showSplashScreen, setShowSplashScreen] = useState(true)

  useEffect(() => {
    // Check 1-day session expiry first
    const loginTimestamp = sessionStorage.getItem('loginTimestamp')
    if (loginTimestamp) {
      const elapsed = Date.now() - parseInt(loginTimestamp, 10)
      if (elapsed > ONE_DAY_MS) {
        logout()
        setShowSplashScreen(false)
        return
      }
    }

    // Auth is driven by vendor session data — no JWT token check needed
    const vendorData = sessionStorage.getItem('vendor')
    if (!vendorData) {
      logout()
    }

    setShowSplashScreen(false)
    // eslint-disable-next-line
  }, [])

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export {AuthProvider, AuthInit, useAuth}
